<template>
  <div>
    <b-tabs
      v-if="surveyTypeData.id"
      vertical
      content-class="col-12 col-md-9 mt-1 mt-md-0"
      pills
      nav-wrapper-class="col-md-3 col-12"
      nav-class="nav-left"
    >
      <!-- General Tab -->
      <b-tab active>
        <template #title>
          <feather-icon icon="InfoIcon" size="18" class="mr-50" />
          <span class="font-weight-bold">{{ $t('General') }}</span>
        </template>

        <b-card>
          <h2 class="text-primary">{{ $t('surveyType.update') }}</h2>

          <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
            <b-form class="mt-2" @submit.prevent="handleSubmit(onSubmit)">
              <b-row>
                <b-col sm="12">
                  <validation-provider #default="validationContext" name="Survey Type Title" rules="required">
                    <b-form-group 
                      :label="$t('surveyType.title')" 
                      label-for="survey-type-title"
                    >
                      <b-form-input 
                        id="survey-type-title"
                        v-model="surveyTypeData.title" 
                        :placeholder="$t('surveyType.placeholder.title')" 
                        :state="getValidationState(validationContext)"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col sm="12">
                  <validation-provider #default="validationContext" name="Survey Type Key" rules="required">
                    <b-form-group 
                      :label="$t('surveyType.key')" 
                      label-for="survey-type-key"
                    >
                      <b-form-input 
                        id="survey-type-key"
                        v-model="surveyTypeData.typekey" 
                        placeholder="employee-satisfaction"
                        :state="getValidationState(validationContext)"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col sm="12">
                  <b-form-group 
                    :label="$t('surveyType.description')" 
                    label-for="survey-type-description"
                  >
                    <b-form-textarea 
                      id="survey-type-description"
                      v-model="surveyTypeData.description" 
                      :placeholder="$t('surveyType.placeholder.description')" 
                      rows="3"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-button 
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'" 
                    variant="primary" 
                    class="mt-2 mr-1"
                    type="submit"
                    :disabled="isLoading"
                  >
                    {{ $t("Save Changes") }}
                  </b-button>

                  <b-button 
                    v-ripple.400="'rgba(186, 191, 199, 0.15)'" 
                    variant="outline-secondary" 
                    class="mt-2"
                    @click="$router.push({ name: 'SurveyTypes' })"
                  >
                    {{ $t('Cancel') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-card>
      </b-tab>

      <!-- Tags Tab -->
      <b-tab>
        <template #title>
          <feather-icon icon="TagIcon" size="18" class="mr-50" />
          <span class="font-weight-bold">{{ $t('surveyType.tags.title') }}</span>
        </template>

        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h2 class="text-primary mb-0">{{ $t('surveyType.tags.title') }}</h2>
            <b-button variant="primary" @click="openTagModal()">
              <feather-icon icon="PlusIcon" class="mr-50" />
              {{ $t('surveyType.tags.addNew') }}
            </b-button>
          </div>

          <b-table
            :items="sortedTags"
            :fields="tagTableColumns"
            responsive
            show-empty
            :empty-text="$t('surveyType.tags.noTags')"
          >
            <template #cell(tagorder)="data">
              <b-badge variant="light-primary" pill>
                {{ data.item.tagorder }}
              </b-badge>
            </template>

            <template #cell(typekey)="data">
              <code>{{ data.item.typekey }}</code>
            </template>

            <template #cell(description)="data">
              <span class="text-muted">{{ data.item.description || '-' }}</span>
            </template>

            <template #cell(actions)="data">
              <div class="d-flex">
                <b-button
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon mr-50"
                  @click="openTagModal(data.item)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon"
                  @click="handleDeleteTag(data.item)"
                >
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </div>
            </template>
          </b-table>
        </b-card>
      </b-tab>
    </b-tabs>

    <!-- Not Found Card -->
    <b-card v-else>
      <div class="text-center">
        <h4>{{ $t('surveyType.notFound') }}</h4>
      </div>
    </b-card>

    <!-- Tag Modal -->
    <b-modal
      v-model="isTagModalActive"
      :title="editingTag ? $t('surveyType.tags.editTag') : $t('surveyType.tags.addNew')"
      hide-footer
      size="lg"
    >
      <validation-observer #default="{ handleSubmit }" ref="refTagFormObserver">
        <b-form @submit.prevent="handleSubmit(handleSaveTag)">
          <b-row>
            <b-col cols="12">
              <validation-provider #default="validationContext" name="Tag Title" rules="required">
                <b-form-group :label="$t('surveyType.tags.tagTitle')" label-for="tag-title">
                  <b-form-input
                    id="tag-title"
                    v-model="tagFormData.title"
                    :state="getValidationState(validationContext)"
                    :placeholder="$t('surveyType.tags.placeholder.title')"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <validation-provider #default="validationContext" name="Type Key" rules="required">
                <b-form-group :label="$t('surveyType.tags.typeKey')" label-for="tag-typekey">
                  <b-form-input
                    id="tag-typekey"
                    v-model="tagFormData.typekey"
                    :state="getValidationState(validationContext)"
                    placeholder="performance-rating"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <b-form-group :label="$t('surveyType.tags.description')" label-for="tag-description">
                <b-form-textarea
                  id="tag-description"
                  v-model="tagFormData.description"
                  :placeholder="$t('surveyType.tags.placeholder.description')"
                  rows="3"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <validation-provider #default="validationContext" name="Tag Order" rules="required">
                <b-form-group :label="$t('surveyType.tags.tagOrder')" label-for="tag-order">
                  <b-form-input
                    id="tag-order"
                    v-model.number="tagFormData.tagorder"
                    type="number"
                    min="1"
                    :state="getValidationState(validationContext)"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                type="submit"
                class="mr-1"
                :disabled="isSavingTag"
              >
                {{ $t('Save') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                variant="outline-secondary"
                @click="isTagModalActive = false"
              >
                {{ $t('Cancel') }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormInvalidFeedback,
  BRow,
  BCol,
  BCard,
  BTabs,
  BTab,
  BTable,
  BBadge,
  BModal,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { onUnmounted, onMounted } from '@vue/composition-api'
import store from '@/store'
import surveyTypeStoreModule from '../surveyTypeStoreModule'
import formValidation from '@core/comp-functions/forms/form-validation'
import { required } from '@validations'
import useSurveyTypeEdit from './useSurveyTypeEdit'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BRow,
    BCol,
    BCard,
    BTabs,
    BTab,
    BTable,
    BBadge,
    BModal,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
    }
  },
  setup() {
    const SURVEY_TYPE_APP_STORE_MODULE_NAME = 'app-survey-type-edit'

    // Register module
    if (!store.hasModule(SURVEY_TYPE_APP_STORE_MODULE_NAME)) {
      store.registerModule(SURVEY_TYPE_APP_STORE_MODULE_NAME, surveyTypeStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SURVEY_TYPE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(SURVEY_TYPE_APP_STORE_MODULE_NAME)
      }
    })

    // Use composition function
    const {
      surveyTypeData,
      tags,
      sortedTags,
      isLoading,
      isSavingTag,
      isTagModalActive,
      editingTag,
      tagFormData,
      tagTableColumns,
      initializeSurveyType,
      fetchTags,
      updateSurveyType,
      openTagModal,
      saveTag,
      deleteTag,
    } = useSurveyTypeEdit()

    // Initialize on mount
    onMounted(() => {
      initializeSurveyType()
    })

    const { refFormObserver, getValidationState, resetForm } = formValidation(() => {})
    const tagFormValidation = formValidation(() => {})

    return {
      surveyTypeData,
      tags,
      sortedTags,
      isLoading,
      isSavingTag,
      isTagModalActive,
      editingTag,
      tagFormData,
      tagTableColumns,
      fetchTags,
      updateSurveyType,
      openTagModal,
      saveTag,
      deleteTag,
      refFormObserver,
      getValidationState,
      resetForm,
      refTagFormObserver: tagFormValidation.refFormObserver,
    }
  },
  methods: {
    onSubmit() {
      this.updateSurveyType()
    },
    handleDeleteTag(tag) {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t("You won't be able to revert this!"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete it!'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteTag(tag.id)
        }
      })
    },
    handleSaveTag() {
      this.saveTag()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
